import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { AppConfiguration } from "config";
import { MetaTags } from 'react-meta-tags';
import Breadcrumbs from 'components/Common/Breadcrumb';
import SfNavLink from 'components/Common/SfNavLink';
import { Container, Card, CardBody, Button, Row, Col, Modal, ModalHeader, ModalBody, TabContent, TabPane } from "reactstrap";
import OffsetPagination from 'components/Common/OffsetPagination';
import { useRemoteTableSort } from 'hooks/useRemoteTableSort';
import { REPORTING_TIMEZONE } from 'helpers/sf_constants';
import { format } from 'date-fns';
import { utcToZonedTime } from 'date-fns-tz';
import { forColumnHeaderWrapper, formatTime } from 'helpers/sf_utils';
import BootstrapTable from 'react-bootstrap-table-next';
import { customSortCaret } from 'helpers/bootstrap_table_helper';
import LoadingDashboardTableSkeleton from 'pages/Dashboard/LoadingDashboardTableSkeleton';

import {
    fetchFullBuildExecutionResults,
    fetchPrBuildExecutionResults,
    fetchSuiteResults,
    fetchCsvReport,
    setSelectedExecution,
    setModalOpen
} from 'store/admin/automation-tests/actions';

const EXECUTIONS_PAGE_SIZE = 15;
const DEFAULT_EXECUTIONS_SORT = { sort_column: 'created_at', sort_direction: 'desc' };

const AutomationTests = () => {
    const dispatch = useDispatch();
    const {
        fullBuildExecutionResults,
        fullBuildLoading,
        fullBuildLastPageFetched,
        fullBuildHasNextPage,
        fullBuildTotalNumberOfPages,
        fullBuildHasLookaheadPages,
        fullBuildLookaheadPages,
        prBuildExecutionResults,
        prBuildLoading,
        prBuildLastPageFetched,
        prBuildHasNextPage,
        prBuildTotalNumberOfPages,
        prBuildHasLookaheadPages,
        prBuildLookaheadPages,
        suiteResults,
        csvReports,
        csvLoading,
        error,
        modalOpen,
        selectedExecution
    } = useSelector(state => state.AutomationTestsReducer);

    const [activeTab, setActiveTab] = useState('1');

    // Kept in refs so a page change can reuse the active sort without the
    // request builders depending on the sort hooks.
    const fullBuildSortRef = useRef(DEFAULT_EXECUTIONS_SORT);
    const prBuildSortRef = useRef(DEFAULT_EXECUTIONS_SORT);

    const executionsRequest = (pageNumber, sort) => ({
        page_number: Math.max(1, Number(pageNumber) || 1),
        page_size: EXECUTIONS_PAGE_SIZE,
        sort_column: sort.sort_column,
        sort_direction: sort.sort_direction
    });

    const requestFullBuild = useCallback((pageNumber, sort = fullBuildSortRef.current) => {
        dispatch(fetchFullBuildExecutionResults(executionsRequest(pageNumber, sort)));
    }, [dispatch]);

    const requestPrBuild = useCallback((pageNumber, sort = prBuildSortRef.current) => {
        dispatch(fetchPrBuildExecutionResults(executionsRequest(pageNumber, sort)));
    }, [dispatch]);

    useEffect(() => {
        requestFullBuild(1);
        requestPrBuild(1);
    }, [requestFullBuild, requestPrBuild]);

    const onFullBuildSortChange = useCallback((sort_column, sort_direction) => {
        fullBuildSortRef.current = { sort_column, sort_direction };
        requestFullBuild(1, fullBuildSortRef.current);
    }, [requestFullBuild]);

    const onPrBuildSortChange = useCallback((sort_column, sort_direction) => {
        prBuildSortRef.current = { sort_column, sort_direction };
        requestPrBuild(1, prBuildSortRef.current);
    }, [requestPrBuild]);

    const fullBuildSort = useRemoteTableSort(
        DEFAULT_EXECUTIONS_SORT.sort_column,
        DEFAULT_EXECUTIONS_SORT.sort_direction,
        onFullBuildSortChange
    );
    const prBuildSort = useRemoteTableSort(
        DEFAULT_EXECUTIONS_SORT.sort_column,
        DEFAULT_EXECUTIONS_SORT.sort_direction,
        onPrBuildSortChange
    );

    const toggleModal = () => dispatch(setModalOpen(!modalOpen));

    const handleDetailedReport = async (execution) => {
        dispatch(setSelectedExecution(execution));
        if (!suiteResults[execution.id]) {
            dispatch(fetchSuiteResults(execution.id));
        }
        if (!csvReports[execution.semaphore_workflow_id]) {
            dispatch(fetchCsvReport(execution.semaphore_workflow_id));
        }
        dispatch(setModalOpen(true));
    };

    const executionColumns = [
        {
            dataField: 'semaphore_workflow_id',
            text: 'Workflow ID',
            headerFormatter: forColumnHeaderWrapper,
            sort: true
        },
        {
            dataField: 'branch',
            text: 'Branch',
            headerFormatter: forColumnHeaderWrapper,
            sort: true
        },
        {
            dataField: 'created_at',
            text: 'Start Time',
            formatter: (cell, row) => {
                const startTime = new Date(new Date(cell).getTime() - row.time * 1000);
                return format(utcToZonedTime(startTime, REPORTING_TIMEZONE), 'MM/dd/yyyy HH:mm a');
            },
            headerFormatter: forColumnHeaderWrapper,
            sort: true
        },
        {
            dataField: 'time',
            text: 'Time',
            formatter: (cell) => formatTime(cell),
            headerFormatter: forColumnHeaderWrapper,
            hasInfoTooltip: true,
            tooltipDescription: 'Total Execution Time',
            sort: true
        },
        {
            dataField: 'test_suites_count',
            text: 'Executed',
            headerFormatter: forColumnHeaderWrapper,
            hasInfoTooltip: true,
            tooltipDescription: 'No. of Test Suites Executed',
            sort: true
        },
        {
            dataField: 'test_suites_failed',
            text: 'Failed',
            headerFormatter: forColumnHeaderWrapper,
            hasInfoTooltip: true,
            tooltipDescription: 'No. of Test Suites Failed',
            sort: true
        },
        {
            dataField: 'semaphore_url',
            text: 'Semaphore URL',
            formatter: (cell, row) => {
                const semaphoreUrl = `https://smartfinancial.semaphoreci.com/workflows/${row.semaphore_workflow_id}/summary`;
                return <a href={semaphoreUrl} className="text-sfblue" target="_blank" rel="noopener noreferrer">Link</a>;
            },
            hasInfoTooltip: true,
            tooltipDescription: 'Link to the Semaphore Workflow Tests Summary',
            headerFormatter: forColumnHeaderWrapper,
        },
        {
            dataField: 'reports_url',
            text: 'S3 URL',
            formatter: (cell) => <a href={cell} className="text-sfblue" target="_blank" rel="noopener noreferrer">Link</a>,
            hasInfoTooltip: true,
            tooltipDescription: 'Link to the S3 Bucket Containing Detailed Reports and Results',
            headerFormatter: forColumnHeaderWrapper,
        },
        {
            dataField: 'actions',
            text: 'Reports',
            formatter: (cell, row) => (
                <Button color="outline-primary" size="sm" onClick={() => handleDetailedReport(row)}>
                    View Report
                </Button>
            ),
            hasInfoTooltip: true,
            tooltipDescription: 'Detailed Report of Suite Results and Latency Status Results',
            headerFormatter: forColumnHeaderWrapper,
        }
    ];

    const suiteColumns = [
        {
            dataField: 'test_suite_name',
            text: 'Test Suite Name',
            headerFormatter: forColumnHeaderWrapper,
            sort: true
        },
        {
            dataField: 'tests_count',
            text: 'Tests Count',
            headerFormatter: forColumnHeaderWrapper,
            sort: true
        },
        {
            dataField: 'failures_count',
            text: 'Failures Count',
            headerFormatter: forColumnHeaderWrapper,
            sort: true
        },
        {
            dataField: 'skipped_count',
            text: 'Skipped Count',
            headerFormatter: forColumnHeaderWrapper,
            sort: true
        },
        {
            dataField: 'errors_count',
            text: 'Errors Count',
            headerFormatter: forColumnHeaderWrapper,
            sort: true
        },
        {
            dataField: 'time_per_test',
            text: 'Time per Test',
            formatter: (cell, row) => {
                const timePerTest = row.tests_count > 0 ? row.time / row.tests_count : 0;
                return formatTime(timePerTest);
            },
            headerFormatter: forColumnHeaderWrapper
        },
        {
            dataField: 'time',
            text: 'Total Time',
            formatter: (cell) => formatTime(cell),
            headerFormatter: forColumnHeaderWrapper,
            sort: true
        }
    ];

    const csvColumns = useMemo(() => [
        {
            dataField: 'uniqueKey',
            text: 'Unique Key',
            hidden: true,
        },
        {
            dataField: 'account_number',
            text: 'Account Number',
            headerFormatter: forColumnHeaderWrapper,
            sort: true
        },
        {
            dataField: 'page',
            text: 'Page',
            headerFormatter: forColumnHeaderWrapper,
            sort: true
        },
        {
            dataField: 'product_type',
            text: 'Product Type',
            headerFormatter: forColumnHeaderWrapper,
            sort: true
        },
        {
            dataField: 'lead_type',
            text: 'Lead Type',
            headerFormatter: forColumnHeaderWrapper,
            sort: true
        },
        {
            dataField: 'time_range',
            text: 'Time Range',
            headerFormatter: forColumnHeaderWrapper,
            sort: true
        },
        {
            dataField: 'load_time',
            text: 'Load Time',
            formatter: (cell) => cell + 's',
            headerFormatter: forColumnHeaderWrapper,
            sort: true
        },
        {
            dataField: 'url',
            text: 'URL',
            headerFormatter: forColumnHeaderWrapper,
            formatter: (cell) => <a href={cell} target="_blank" rel="noopener noreferrer">View</a>
        }
    ], []);

    const csvReportWithUniqueKeys = useMemo(() => {
        if (selectedExecution && csvReports[selectedExecution.semaphore_workflow_id]) {
            return csvReports[selectedExecution.semaphore_workflow_id].map((row, index) => ({
                ...row,
                uniqueKey: `${row.account_number}-${row.product_type}-${row.lead_type}-${index}`
            }));
        }
        return [];
    }, [selectedExecution, csvReports]);

    const toggleTab = tab => {
        if (activeTab !== tab) setActiveTab(tab);
    }

    return (
        <React.Fragment>
            <div className="page-content" id="page-content-wrapper">
                <MetaTags>
                    <title>Automation Tests | {AppConfiguration.WEBSITE}</title>
                </MetaTags>
                <Container fluid>
                    <Breadcrumbs title="" breadcrumbItem="Automation Tests" />
                    <Card>
                        <CardBody>
                            <div className="d-flex justify-content-between">
                                <ul className="nav sfnav mb-4">
                                    <li className="nav-item">
                                        <SfNavLink position="1" tabName="Nightly System Checks" customActiveTab={activeTab} setcustomActiveTab={setActiveTab} />
                                    </li>
                                    <li className="nav-item">
                                        <SfNavLink position="2" tabName="Feature Integration Checks" customActiveTab={activeTab} setcustomActiveTab={setActiveTab} />
                                    </li>
                                </ul>
                            </div>
                            <TabContent activeTab={activeTab}>
                                <TabPane tabId="1">
                                    <Row className="sfTable-Container">
                                        <Col xl="12">
                                            {fullBuildLoading ? (
                                                <LoadingDashboardTableSkeleton rows={15} cols={executionColumns.length} />
                                            ) : (
                                                <BootstrapTable
                                                    keyField="id"
                                                    data={fullBuildExecutionResults}
                                                    columns={executionColumns}
                                                    responsive
                                                    bordered={false}
                                                    striped={false}
                                                    defaultSorted={fullBuildSort.defaultSorted}
                                                    remote={fullBuildSort.remote}
                                                    onTableChange={fullBuildSort.onTableChange}
                                                    rowClasses="tr-hover"
                                                    headerClasses="sftableHeader thead-h50 text-black"
                                                    classes="table align-middle table-nowrap text-black"
                                                    headerWrapperClasses="thead-light"
                                                    noDataIndication={() => "You currently don't have any data for the full builds"}
                                                    sort={{ sortCaret: customSortCaret }}
                                                />
                                            )}
                                        </Col>
                                    </Row>
                                    <Row>
                                        <Col xl="auto" className="ms-auto">
                                            <OffsetPagination
                                                currentPage={fullBuildLastPageFetched}
                                                totalPages={fullBuildTotalNumberOfPages}
                                                hasNextPage={fullBuildHasNextPage}
                                                hasDeepOffsetPages={fullBuildHasLookaheadPages}
                                                offsetLookAheadPages={fullBuildLookaheadPages}
                                                loading={fullBuildLoading}
                                                onPageChange={requestFullBuild}
                                                className="campaign-analysis-pagination mt-3 align-items-center justify-content-end"
                                            />
                                        </Col>
                                    </Row>
                                </TabPane>
                                <TabPane tabId="2">
                                    <Row className="sfTable-Container">
                                        <Col xl="12">
                                            {prBuildLoading ? (
                                                <LoadingDashboardTableSkeleton rows={15} cols={executionColumns.length} />
                                            ) : (
                                                <BootstrapTable
                                                    keyField="id"
                                                    data={prBuildExecutionResults}
                                                    columns={executionColumns}
                                                    responsive
                                                    bordered={false}
                                                    striped={false}
                                                    defaultSorted={prBuildSort.defaultSorted}
                                                    remote={prBuildSort.remote}
                                                    onTableChange={prBuildSort.onTableChange}
                                                    rowClasses="tr-hover"
                                                    headerClasses="sftableHeader thead-h50 text-black"
                                                    classes="table align-middle table-nowrap text-black"
                                                    headerWrapperClasses="thead-light"
                                                    noDataIndication={() => "You currently don't have any data for pr builds"}
                                                    sort={{ sortCaret: customSortCaret }}
                                                />
                                            )}
                                        </Col>
                                    </Row>
                                    <Row>
                                        <Col xl="auto" className="ms-auto">
                                            <OffsetPagination
                                                currentPage={prBuildLastPageFetched}
                                                totalPages={prBuildTotalNumberOfPages}
                                                hasNextPage={prBuildHasNextPage}
                                                hasDeepOffsetPages={prBuildHasLookaheadPages}
                                                offsetLookAheadPages={prBuildLookaheadPages}
                                                loading={prBuildLoading}
                                                onPageChange={requestPrBuild}
                                                className="campaign-analysis-pagination mt-3 align-items-center justify-content-end"
                                            />
                                        </Col>
                                    </Row>
                                </TabPane>
                            </TabContent>
                            {error && <p className="text-danger mt-3">{error}</p>}
                        </CardBody>
                    </Card>
                </Container>
            </div>

            <Modal isOpen={modalOpen} toggle={toggleModal} style={{ maxWidth: '90%' }}>
                <ModalHeader toggle={toggleModal}>
                    Detailed Report for Execution #{selectedExecution?.semaphore_workflow_id}
                </ModalHeader>
                <ModalBody>
                    <h4>Suite Results</h4>
                    {selectedExecution && suiteResults[selectedExecution.id] && (
                        <BootstrapTable
                            keyField="id"
                            data={suiteResults[selectedExecution.id]}
                            columns={suiteColumns}
                            responsive
                            bordered={false}
                            striped={false}
                            rowClasses="tr-hover"
                            headerClasses="sftableHeader thead-h50 text-black"
                            classes="table align-middle table-nowrap text-black"
                            headerWrapperClasses="thead-light"
                            noDataIndication="You currently don't have any data"
                            sort={{ sortCaret: customSortCaret }}
                        />
                    )}

                    <h4 className="mt-4">Latency Status Results</h4>
                    <Row className="sfTable-Container" style={{ maxHeight: '60vh' }}>
                        <Col xl="12">
                            {csvLoading ? (
                                <LoadingDashboardTableSkeleton rows={15} cols={csvColumns.length} />
                            ) : (
                                <BootstrapTable
                                    keyField="uniqueKey"
                                    data={csvReportWithUniqueKeys}
                                    columns={csvColumns}
                                    responsive
                                    bordered={false}
                                    striped={false}
                                    rowClasses="tr-hover"
                                    headerClasses="sftableHeader thead-h50 text-black"
                                    classes="table align-middle table-nowrap text-black"
                                    headerWrapperClasses="thead-light"
                                    noDataIndication="No CSV data available"
                                    sort={{ sortCaret: customSortCaret }}
                                />
                            )}
                        </Col>
                    </Row>
                </ModalBody>
            </Modal>
        </React.Fragment>
    );
}

export default AutomationTests;